import { Helmet } from 'react-helmet-async';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';
import { buildArticleSchema, buildBreadcrumbSchema, toJsonLd } from '../schema';

const TITLE = 'PPR vs Standard vs Half-PPR Scoring Explained — RosterRx';
const DESCRIPTION =
  'PPR vs Standard vs Half-PPR fantasy football scoring explained: what each format rewards, which positions gain or lose value, and how to tell which one your league uses.';
// Update by hand only when this page's explanation actually changes.
const LAST_UPDATED = '2026-08-31';
const ARTICLE_SCHEMA = buildArticleSchema({
  path: '/ppr-vs-standard-scoring',
  title: TITLE,
  description: DESCRIPTION,
  datePublished: LAST_UPDATED,
  dateModified: LAST_UPDATED,
});
const BREADCRUMB_SCHEMA = buildBreadcrumbSchema({
  path: '/ppr-vs-standard-scoring',
  title: 'PPR vs Standard Scoring',
});

/**
 * Target query/intent: "PPR vs standard scoring" / "what is half PPR". Deeper explainer than
 * Glossary's one-paragraph PPR/Standard definitions -- second of the Phase 4 evergreen set (R5.6).
 */
function PprVsStandardScoringView() {
  usePrerenderReady();

  return (
    <div>
      <Helmet>
        <title>{TITLE}</title>
        <meta name='description' content={DESCRIPTION} />
        <link rel='canonical' href={`${SITE_URL}/ppr-vs-standard-scoring`} />
        <meta property='og:title' content={TITLE} />
        <meta property='og:description' content={DESCRIPTION} />
        <meta property='og:url' content={`${SITE_URL}/ppr-vs-standard-scoring`} />
        <meta property='og:type' content='article' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content={TITLE} />
        <meta name='twitter:description' content={DESCRIPTION} />
        <script type='application/ld+json'>{toJsonLd(ARTICLE_SCHEMA)}</script>
        <script type='application/ld+json'>{toJsonLd(BREADCRUMB_SCHEMA)}</script>
      </Helmet>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>PPR vs Standard vs Half-PPR, in short</h2>
        <p>
          The only real difference between these three formats is how many fantasy points a
          reception itself is worth: <strong>PPR</strong> awards a full point per catch,{' '}
          <strong>Half-PPR</strong> awards half a point per catch, and <strong>Standard</strong>{' '}
          awards nothing for the catch itself — only for the yardage and touchdowns that follow
          it. Everything else about scoring (yardage, touchdowns, turnovers) is the same across
          all three.
        </p>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>Who gains and loses value in each format</h2>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 8 }}>
            <strong>PPR</strong> boosts pass-catching running backs and high-target,
            possession-style receivers — a player with 8 catches for 60 yards can outscore a
            player with 1 catch for 90 yards.
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>Standard</strong> favors rushing volume and big-play, deep-threat receivers,
            since receptions alone carry no direct point value.
          </li>
          <li>
            <strong>Half-PPR</strong> sits in between — reception volume still matters, just not
            as heavily as full PPR, which is why it's become the most common default format in
            newer leagues.
          </li>
        </ul>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>How to tell which one your league uses</h2>
        <p>
          Check your league's scoring settings for a "reception" or "per catch" point value — 1.0
          is PPR, 0.5 is Half-PPR, and 0 (or no such setting at all) is Standard. RosterRx's
          rankings default to PPR since it's the most common format today; use the Standard toggle
          on the rankings page if your league scores differently.
        </p>
      </div>
    </div>
  );
}

export default PprVsStandardScoringView;
